/**
 * Reset Password Dialog
 *
 * Dialog for admins to reset a user's password.
 * Activates invited users automatically.
 */

import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Login } from '../../apis/api-client';
import { usersClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../ui';

interface ResetPasswordDialogProps {
  open: boolean;
  user: Login | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ResetPasswordDialog({ open, user, onClose, onSuccess }: ResetPasswordDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<any>({});

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm the password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !user) return;

    try {
      setLoading(true);
      await usersClient.resetUserPassword(user.id, {
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      alert(enqueueSnackbar, `Password reset successfully for ${user.firstName} ${user.lastName}. User can now login with the new password.`, 'success');
      setFormData({ newPassword: '', confirmPassword: '' });
      onSuccess?.();
      onClose();
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to reset password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ newPassword: '', confirmPassword: '' });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset Password</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {user && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Reset password for:{' '}
              <strong>
                {user.firstName} {user.lastName}
              </strong>{' '}
              ({user.email})
            </Typography>
          )}

          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={formData.newPassword}
            onChange={(e) => handleChange('newPassword', e.target.value)}
            error={!!errors.newPassword}
            helperText={errors.newPassword || 'Must be at least 8 characters'}
            required
            autoFocus
          />

          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            value={formData.confirmPassword}
            onChange={(e) => handleChange('confirmPassword', e.target.value)}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            required
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Note: If this user was invited but hasn't accepted yet, resetting their password will activate their account and allow them to login immediately.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Reset Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
